import * as React from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { yupResolver } from '@hookform/resolvers/yup';
import { signUpSchema } from '@/schema/sign-up-schema';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { signUp } from '@/services/auth';
import { useAuthenticationDispatch, useAuthenticationState } from '@/hooks/use-authentication';
import { z } from 'zod';
import { SignUpForm } from '@/routes/_layout/sign-up/-components/sign-up-form';
import { LeftSectionMessage } from '@/routes/_layout/sign-up/-components/left-section-message';
import { errorHandler } from '@/lib/utils';
import { TextSeparator } from '@/components/text-separator';
import { GoogleButton } from '@/routes/_layout/-components/google-button';
import { AppleButton } from '@/routes/_layout/-components/apple-button';
import { LinkedInButton } from '@/routes/_layout/-components/linkedin-button';
export const Route = createFileRoute('/_layout/sign-up/')({
    component: SignUpPage,
    validateSearch: z.object({
        redirect: z.string().optional().default('/console'),
    }),
    beforeLoad: ({ context, search }) => {
        if (context.authentication?.status === 'authenticated') {
            throw redirect({ to: search.redirect || '/console' });
        }
    },
});
function SignUpPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuthenticationState();
    const dispatch = useAuthenticationDispatch();
    const { redirect } = Route.useSearch();
    const form = useForm({
        resolver: yupResolver(signUpSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            acceptedTerms: false,
        },
    });
    const { isPending, mutate } = useMutation({
        mutationFn: signUp,
        onSuccess: (data) => {
            dispatch({ type: 'SIGN_IN', user: data.user });
        },
        onError: errorHandler,
    });
    React.useEffect(() => {
        if (user) {
            const path = redirect || '/console';
            navigate(path).then();
        }
    }, [navigate, redirect, user]);
    const onSubmit = React.useCallback((data) => {
        mutate(data);
    }, [mutate]);
    return (<div className="relative min-h-dvh bg-linear-to-b from-background via-background to-muted/30">
            <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
                    {/* Left copy – Adora messaging */}
                    <LeftSectionMessage />

                    {/* Sign-up card */}
                    <div className="order-1 md:order-2">
                        <Card className="border-primary/20 shadow-sm backdrop-blur">
                            <CardHeader>
                                <CardTitle className="text-2xl">{t('sign_up.title', { ns: 'glossary' })}</CardTitle>
                                <CardDescription>{t('sign_up.description', { ns: 'glossary' })}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <SignUpForm form={form} onSubmit={onSubmit} isPending={isPending}/>

                                <TextSeparator>Or continue with</TextSeparator>

                                <div className="space-y-2" data-testid="sign-up-social-buttons" aria-hidden="true">
                                    <AppleButton />
                                    <GoogleButton />
                                    <LinkedInButton />
                                </div>

                                <p className="text-xs text-muted-foreground text-center">
                                    {t('already_have_account')}&nbsp;
                                    <Link to="/sign-in" className="text-primary hover:underline underline-offset-2" search={{ redirect: redirect || '/console' }}>
                                        {t('sign_in')}
                                    </Link>
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
        </div>);
}
